// theme.ts — Cassandra / Nebula design tokens (accessibility tuned)
// Drop-in replacement (backwards compatible). 2025-08

// -----------------------------
// Raw/fixed hues (use sparingly)
// -----------------------------
export const fixedColors = {
  CRIMSON: "#DC143C",
  BLURPLE: "#5865F2",
  PURPLE: "#9036AF",
  FULL_WHITE: "#FFFFFF",
  BLACK: "#202225",
  BLACK_DARK: "#2C2F33",
  BLACK_LIGHT: "#2F3136",
  BLACK_PRIMARY: "#36393f",
  DARK_SALMON: "#E9967A",
  DANGER: "hsl(359,calc(var(--saturation-factor, 1)*66.7%),54.1%)", // legacy
  LIGHTGREEN: "#90EE90",
  SEAGREEN: "#11883B",
  ORANGE: "#f59e0b", // popular app highlight — preserved
  SILVER: "#B9BBBE",
  PRIMARY: "#4D4D4D", // legacy gray
  TEXT_DANGER: "#db662e",
  BLUE: "#3b82f6", // tailwind blue-500-ish
} as const;

// ----------------------------------------------------
// Prompt input colors (contrast-adjusted)
// ----------------------------------------------------
export const promptInputColors = {
  dark: {
    PROMPT_BG: "#072719",
    PROMPT_PLACEHOLDER_COLOR: "#268b5f", // ~3.8:1 on bg
    PROMPT_TEXT_COLOR: "#3DCF8E",
    PROMPT_BORDER_COLOR: "#257a57",
  },
  light: {
    // Fresher, airier green lens with strong legibility
    PROMPT_BG: "#EAF5EF",
    PROMPT_PLACEHOLDER_COLOR: "#2E6B55", // ~5.6:1 on bg
    PROMPT_TEXT_COLOR: "#165C43",        // ~7.1:1 on bg
    PROMPT_BORDER_COLOR: "#2F6E58",      // ~5.4:1 on bg
  },
} as const;

// ----------------------------------------------------
// Graph colors (type-driven) with AA contrast on nodes
// Also exposes legacy shape for backwards compatibility
// ----------------------------------------------------
export const nodeColors = {
  dark: {
    NODE_COLORS: {
      TEXT: "#DCE5E7",
      NODE_KEY: "#78caff",       // keys/objects (AA)
      NODE_VALUE: "#E0E0E0",
      INTEGER: "#ffcc80",
      NULL: "#cdcdcd",           // raised for AA
      BOOL: {
        FALSE: "#ffb5b0",        // softened for AA
        TRUE: "#2ee6a2",
      },
      PARENT_ARR: "#ffc48f",
      PARENT_OBJ: "#78caff",
      CHILD_COUNT: "#ffffff",
    },
    BACKGROUND_NODE: "#4A4D5F",
    EDGE_COLORS: { DEFAULT: "#f59e0b" },
  },

  light: {
    // Graph now renders on soft cream cards (mirrors app surfaces)
    NODE_COLORS: {
      TEXT: "#1F2937",           // slate-800
      NODE_KEY: "#124095",       // deep blue (AA on white)
      NODE_VALUE: "#475569",     // slate-600 (AA)
      INTEGER: "#92400E",        // amber-800 (AA)
      NULL: "#6B7280",           // slate-500/600 (AA)
      BOOL: {
        FALSE: "#991B1B",        // red-800 (AA)
        TRUE: "#166534",         // green-800 (AA)
      },
      PARENT_ARR: "#B45309",     // amber-700 (AA)
      PARENT_OBJ: "#1D4ED8",     // blue-600 (AA; aligns with objects)
      CHILD_COUNT: "#475569",    // slate-600 (AA)
    },
    BACKGROUND_NODE: "#FFFBF5",   // warm-cream cards
    EDGE_COLORS: { DEFAULT: "#0250c8" },
  },
} as const;

// ------------------------------------------
// Dark theme (app surfaces, text, grid, etc)
// (unchanged from previous version you liked)
// ------------------------------------------
export const darkTheme = {
  MODE: "dark",
  ...fixedColors,
  ...nodeColors.dark,
  ...promptInputColors.dark,
  CASSANDRA_RED: "#8d1c1c",

  // Surfaces
  BLACK_SECONDARY: "#23272A",
  SILVER_DARK: "#4D4D4D",
  BACKGROUND_TERTIARY: "#202225",
  BACKGROUND_SECONDARY: "#2f3136",
  BACKGROUND_SECONDARY_ALT: "#2a2d32",
  BACKGROUND_PRIMARY: "#36393f",
  TOOLBAR_BG: "#262626",
  MODAL_BACKGROUND: "#36393E",
  BACKGROUND_MODIFIER_ACCENT: "rgba(79,84,92,0.48)",

  // Text & interactive
  TEXT_NORMAL: "#dcddde",
  TEXT_MUTED: "#c7ccd3",
  TEXT_POSITIVE: "hsl(139,calc(var(--saturation-factor, 1)*51.6%),52.2%)",
  SIDEBAR_ICONS: "#A1A4A6",
  INTERACTIVE_NORMAL: "#b9bbbe",
  INTERACTIVE_HOVER: "#dcddde",
  INTERACTIVE_ACTIVE: "#ffffff",

  // Graph overrides kept (legacy compatibility)
  NODE_KEY: "#78caff",
  OBJECT_KEY: "#59b8ff",

  // Node background — single source (avoid double-defining)
  BACKGROUND_NODE: "#4A4D5F",

  // Grid
  GRID_BG_COLOR: "#1E1E1E",
  GRID_COLOR_PRIMARY: "#272626",
  GRID_COLOR_SECONDARY: "#232323",

  // Workspace panel accents
  WORKSPACE_HEADER_GRADIENT: "linear-gradient(135deg, rgba(82, 126, 255, 0.12), rgba(56, 217, 169, 0.12))",
  WORKSPACE_SURFACE_GRADIENTS: {
    blue: "linear-gradient(135deg, rgba(96, 165, 250, 0.2), rgba(37, 99, 235, 0.1))",
    violet: "linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(139, 92, 246, 0.1))",
    orange: "linear-gradient(135deg, rgba(255, 166, 76, 0.16), rgba(82, 126, 255, 0.1))",
    grape: "linear-gradient(135deg, rgba(192, 132, 252, 0.18), rgba(99, 102, 241, 0.1))",
    gray: "linear-gradient(135deg, rgba(148, 163, 184, 0.14), rgba(71, 85, 105, 0.08))",
  },
  COMPACT_LIST_SURFACE: {
    BORDER: "rgba(255, 255, 255, 0.08)",
    BORDER_HOVER: "rgba(255, 255, 255, 0.16)",
    BACKGROUND: "rgba(255, 255, 255, 0.02)",
    BACKGROUND_HOVER: "rgba(255, 255, 255, 0.05)",
    FOCUS_OUTLINE: "rgba(255, 255, 255, 0.6)",
    SHADOW: "0 4px 14px rgba(0, 0, 0, 0.18)",
  },
  WORKSPACE_TITLE_COLOR: "#dcddde",
} as const;

// ------------------------------------------
// Light theme (app surfaces, text, grid, etc)
// (refreshed for clarity, contrast, and “pop”)
// ------------------------------------------
export const lightTheme = {
  MODE: "light",
  ...fixedColors,
  ...nodeColors.light,
  ...promptInputColors.light,
  CASSANDRA_RED: "#681515",

  // Surfaces — warm neutral cream stack
  BLACK_SECONDARY: "#E4D8C9",
  SILVER_DARK: "#CBBDAA",
  BACKGROUND_TERTIARY: "#E5DACC", // toasted oat highlight
  BACKGROUND_SECONDARY: "#EEE8DE",
  BACKGROUND_SECONDARY_ALT: "#EAE3D8",
  BACKGROUND_PRIMARY: "#F0EDE7",
  TOOLBAR_BG: "#EEEAE2",
  MODAL_BACKGROUND: "#F7F5F0",
  // Soft sepia accent wash for panels/borders
  BACKGROUND_MODIFIER_ACCENT: "rgba(194, 161, 120, 0.22)",

  // Text & interactive (kept high-contrast on cream)
  TEXT_NORMAL: "#3B3226",        // mellow espresso (~11.8:1 on bg)
  TEXT_MUTED: "#514334",         // richer contrast on cream for secondary copy
  TEXT_POSITIVE: "#3D7B61",      // warm evergreen success
  SIDEBAR_ICONS: "#70644F",      // 5+:1 on secondary bg
  INTERACTIVE_NORMAL: "#756750", // rested truffle grey
  INTERACTIVE_HOVER: "#4F4333",  // deeper hover for clarity
  INTERACTIVE_ACTIVE: "#33291D", // strong active state

  // Graph overrides kept (legacy compatibility)
  NODE_KEY: "#124095",          // mirrors NODE_COLORS.NODE_KEY
  OBJECT_KEY: "#1D4ED8",        // aligns objects to blue

  // Node background — single source (avoid double-defining)
  BACKGROUND_NODE: "#F7F5F0",

  // Grid — cleaner, airier
  GRID_BG_COLOR: "#F1EDE5",
  GRID_COLOR_PRIMARY: "#E0D7C9",
  GRID_COLOR_SECONDARY: "#E8E0D4",

  // Workspace panel accents
  WORKSPACE_HEADER_GRADIENT: "linear-gradient(135deg, rgba(82, 126, 255, 0.26), rgba(56, 217, 169, 0.32))",
  WORKSPACE_SURFACE_GRADIENTS: {
    blue: "linear-gradient(135deg, rgba(96, 165, 250, 0.32), rgba(37, 99, 235, 0.18))",
    violet: "linear-gradient(135deg, rgba(168, 85, 247, 0.34), rgba(139, 92, 246, 0.2))",
    orange: "linear-gradient(135deg, rgba(255, 166, 76, 0.3), rgba(82, 126, 255, 0.2))",
    grape: "linear-gradient(135deg, rgba(192, 132, 252, 0.32), rgba(99, 102, 241, 0.18))",
    gray: "linear-gradient(135deg, rgba(148, 163, 184, 0.26), rgba(71, 85, 105, 0.14))",
  },
  COMPACT_LIST_SURFACE: {
    BORDER: "rgba(196, 158, 110, 0.55)",
    BORDER_HOVER: "rgba(196, 158, 110, 0.72)",
    BACKGROUND: "rgba(252, 248, 242, 0.94)",
    BACKGROUND_HOVER: "rgba(248, 242, 234, 0.96)",
    FOCUS_OUTLINE: "rgba(196, 158, 110, 0.75)",
    SHADOW: "0 10px 24px rgba(185, 150, 102, 0.18)",
  },
  WORKSPACE_TITLE_COLOR: "#0f0f0f",
} as const;

// ------------------------------
// Legacy default (non-breaking)
// Note: prefers dark due to spread order.
// ------------------------------
const themeDs = {
  ...lightTheme,
  ...darkTheme,
} as const;
export default themeDs;

// ----------------------------------------------
// New structured export (recommended going fwd):
// ----------------------------------------------
export const themes = {
  light: lightTheme,
  dark: darkTheme,
} as const;

// ----------------------------------------------
// Helpful aliases (graph- and status-oriented)
// ----------------------------------------------
export const graph = {
  dark: {
    nodeBg: darkTheme.BACKGROUND_NODE,
    node: darkTheme.NODE_COLORS,
    edge: darkTheme.EDGE_COLORS,
  },
  light: {
    nodeBg: lightTheme.BACKGROUND_NODE,
    node: lightTheme.NODE_COLORS,
    edge: lightTheme.EDGE_COLORS,
  },
} as const;

export const status = {
  success: {
    dark: { fg: darkTheme.TEXT_POSITIVE },
    light: { fg: lightTheme.TEXT_POSITIVE },
  },
  danger: {
    dark: { fg: fixedColors.TEXT_DANGER },
    light: { fg: fixedColors.TEXT_DANGER },
  },
  warning: {
    dark: { fg: fixedColors.ORANGE },
    light: { fg: fixedColors.ORANGE },
  },
  info: {
    dark: { fg: fixedColors.BLUE },
    light: { fg: fixedColors.BLUE },
  },
} as const;
